import React, { useState, useEffect } from "react";
import { MButton } from "@/components/ui/atoms/MButtons";
import MDialog from "@/components/ui/atoms/MDialog";
import { MOtp } from "@/components/ui/molecules/MOtp";
import { IconBrandWhatsapp, IconMail } from "@tabler/icons-react";
import { getMemberInfo, useProfileUpdate } from "@/hooks/useMembers";
import { otpService } from "@/services/otp.service";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import styles from "./DocumentSend.module.css";

const DocumentSend: React.FC = () => {
  const [showMissingEmailDialog, setShowMissingEmailDialog] = useState(false);
  const [showMissingMobileDialog, setShowMissingMobileDialog] = useState(false);
  const [showAddEmailDialog, setShowAddEmailDialog] = useState(false);
  const [showAddMobileDialog, setShowAddMobileDialog] = useState(false);
  const [showEmailOtpDrawer, setShowEmailOtpDrawer] = useState(false);
  const [showMobileOtpDrawer, setShowMobileOtpDrawer] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successType, setSuccessType] = useState<"email" | "mobile">("email");
  const [emailResendTimer, setEmailResendTimer] = useState(30);
  const [mobileResendTimer, setMobileResendTimer] = useState(30);
  const [emailInput, setEmailInput] = useState("");
  const [mobileInput, setMobileInput] = useState("");

  const memberInfo = getMemberInfo(true);
  const { showToast } = useToast();
  const { handleContactUpdate } = useProfileUpdate();
  const dialCode = "+91"; // Static dial code

  // Utility function to extract error message from API response
  const extractErrorMessage = (error: any, defaultMessage: string): string => {
    if (error?.response?.data?.message) {
      // Handle both string and object message
      if (typeof error.response.data.message === "string") {
        return error.response.data.message;
      } else if (error.response.data.message?.message) {
        return error.response.data.message.message;
      }
    } else if (error?.message) {
      return error.message;
    }
    return defaultMessage;
  };

  // Resend timer effects
  useEffect(() => {
    if (showEmailOtpDrawer && emailResendTimer > 0) {
      const timer = setTimeout(
        () => setEmailResendTimer(emailResendTimer - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [showEmailOtpDrawer, emailResendTimer]);

  useEffect(() => {
    if (showMobileOtpDrawer && mobileResendTimer > 0) {
      const timer = setTimeout(
        () => setMobileResendTimer(mobileResendTimer - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [showMobileOtpDrawer, mobileResendTimer]);

  // Reusable functions to avoid code duplication
  const openWhatsApp = () => {
    const waNumber = "919870597100";

    // Native app deep link
    const appUrl = `whatsapp://send?phone=${waNumber}`;

    // Fallback for Desktop Web
    const webUrl = `https://wa.me/${waNumber}`;

    // Try opening native app first
    globalThis.location.href = appUrl;

    // Fallback to WhatsApp web if not opened
    setTimeout(() => {
      globalThis.open?.(webUrl, "_blank");
    }, 500);
  };

  const openEmail = () => {
    const subject = encodeURIComponent(
      "Upload documents to Maatra (automatic processing)"
    );
    const body = encodeURIComponent(
      "Attach PDF, JPG, or PNG files up to 10MB each.\n\nFiles sent here are secure, confidential, and processed automatically. No one from our team reads or views them.\n\nYou'll receive a confirmation in the app once they're processed."
    );
    globalThis.location.href = `mailto:documents@maatra.health?subject=${subject}&body=${body}`;
  };

  const handleSendWhatsApp = () => {
    if (!memberInfo.mobile || memberInfo.mobile.trim() === "") {
      setShowMissingMobileDialog(true);
      return;
    }
    openWhatsApp();
  };

  const handleSendEmail = () => {
    if (!memberInfo.email || memberInfo.email.trim() === "") {
      setShowMissingEmailDialog(true);
      return;
    }
    openEmail();
  };

  const handleVerifyEmail = async () => {
    try {
      // Send OTP with correct payload
      await otpService.sendOtp({
        send_to: emailInput,
        mode: "email",
        type: "member_update",
      });
      setEmailResendTimer(30); // Reset timer
      setShowAddEmailDialog(false); // Close MDialog first
      setShowEmailOtpDrawer(true); // Then open MOtp
    } catch (error: any) {
      console.error("Failed to send email verification code:", error);
      const errorMessage = extractErrorMessage(
        error,
        "Failed to send verification code"
      );
      showToast({ message: errorMessage, variant: "error" });
    }
  };

  const handleVerifyMobile = async () => {
    try {
      // Send OTP with correct payload
      await otpService.sendOtp({
        send_to: mobileInput,
        dialCode: dialCode,
        mode: "sms",
        type: "member_update",
      });
      setMobileResendTimer(30); // Reset timer
      setShowAddMobileDialog(false); // Close MDialog first
      setShowMobileOtpDrawer(true); // Then open MOtp
    } catch (error: any) {
      console.error("Failed to send mobile verification code:", error);
      const errorMessage = extractErrorMessage(
        error,
        "Failed to send verification code"
      );
      showToast({ message: errorMessage, variant: "error" });
    }
  };

  const handleEmailResend = async () => {
    try {
      await otpService.sendOtp({
        send_to: emailInput,
        mode: "email",
        type: "member_update",
      });
      setEmailResendTimer(30);
      showToast({ message: "Verification code sent", variant: "success" });
    } catch (error: any) {
      console.error("Failed to resend email verification code:", error);
      const errorMessage = extractErrorMessage(error, "Failed to resend code");
      showToast({ message: errorMessage, variant: "error" });
    }
  };

  const handleMobileResend = async () => {
    try {
      await otpService.sendOtp({
        send_to: mobileInput,
        dialCode: dialCode,
        mode: "sms",
        type: "member_update",
      });
      setMobileResendTimer(30);
      showToast({ message: "Verification code sent", variant: "success" });
    } catch (error: any) {
      console.error("Failed to resend mobile verification code:", error);
      const errorMessage = extractErrorMessage(error, "Failed to resend code");
      showToast({ message: errorMessage, variant: "error" });
    }
  };

  // Validation functions
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidMobile = (mobile: string) => {
    return mobile.length === 10 && /^\d+$/.test(mobile);
  };

  const handleEmailOtpVerify = async (otp: string): Promise<true | string> => {
    try {
      // Verify OTP with correct payload
      await otpService.verifyOtp({
        send_to: emailInput,
        otp: otp,
        mode: "email",
        type: "member_update",
      });

      // Update member contact using proper service
      const result = await handleContactUpdate({
        memberId: memberInfo.id,
        updateData: { email: emailInput },
        isChanged: true,
      });

      if (!result.success) {
        throw new Error("Failed to update contact information");
      }

      setShowEmailOtpDrawer(false);
      setSuccessType("email");
      setShowSuccessDialog(true);
      setEmailInput("");
      setShowAddEmailDialog(false);

      return true;
    } catch (error) {
      throw new Error("Invalid verification code");
    }
  };

  const handleMobileOtpVerify = async (otp: string): Promise<true | string> => {
    try {
      // Verify OTP with correct payload
      await otpService.verifyOtp({
        send_to: mobileInput,
        dialCode: dialCode,
        otp: otp,
        mode: "sms",
        type: "member_update",
      });

      // Update member contact using proper service
      const result = await handleContactUpdate({
        memberId: memberInfo.id,
        updateData: { dialCode: dialCode, mobile: mobileInput },
        isChanged: true,
      });

      if (!result.success) {
        throw new Error("Failed to update contact information");
      }

      setShowMobileOtpDrawer(false);
      setSuccessType("mobile");
      setShowSuccessDialog(true);
      setMobileInput("");
      setShowAddMobileDialog(false);

      return true;
    } catch (error) {
      throw new Error("Invalid verification code");
    }
  };

  const handleEmailEdit = () => {
    setShowEmailOtpDrawer(false);
    setShowAddEmailDialog(true);
  };

  const handleMobileEdit = () => {
    setShowMobileOtpDrawer(false);
    setShowAddMobileDialog(true);
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    if (successType === "email") {
      openEmail();
    } else {
      openWhatsApp();
    }
  };

  return (
    <div
      className={styles.documentSend}
      style={
        !memberInfo.isExperienced ? { width: "100%", position: "relative" } : {}
      }
    >
      <h2 className={styles.title}>
        Send documents via
        <br />
        email or WhatsApp
      </h2>
      <p className={styles.description}>
        Your documents are processed securely and privately
      </p>

      <div className={styles.buttonsContainer}>
        <div className={styles.buttonRow}>
          <div className={styles.labelFrame}>
            <IconMail size={18.29} color="#8764BB" />
            <span className={styles.labelText}>documents@maatra.health</span>
          </div>
          <MButton
            variant="filled"
            size="sm"
            onClick={handleSendEmail}
            className={styles.sendButton}
          >
            Send
          </MButton>
        </div>

        <div className={styles.buttonRow}>
          <div className={styles.labelFrame}>
            <IconBrandWhatsapp size={18.29} color="#8764BB" />
            <span className={styles.labelText}>+91 9870597100</span>
          </div>
          <MButton
            variant="filled"
            size="sm"
            onClick={handleSendWhatsApp}
            className={styles.sendButton}
          >
            Send
          </MButton>
        </div>
      </div>

      {/* Missing Email Dialog */}
      <MDialog
        isOpen={showMissingEmailDialog}
        onClose={() => setShowMissingEmailDialog(false)}
        title="Add your email"
        description="You haven’t added your email yet. 
Add it to upload documents by email."
        showIconVariant={true}
        icon={<IconMail size={24} color="#8764BB" />}
        primaryCtaText="Add Email"
        secondaryCtaText="Maybe later"
        onPrimaryCta={() => {
          setShowMissingEmailDialog(false);
          setShowAddEmailDialog(true);
        }}
        onSecondaryCta={() => setShowMissingEmailDialog(false)}
      />

      {/* Missing Mobile Dialog */}
      <MDialog
        isOpen={showMissingMobileDialog}
        onClose={() => setShowMissingMobileDialog(false)}
        title="Add your mobile number"
        description="You haven’t added your mobile number yet. Add it to upload documents by WhatApp."
        showIconVariant={true}
        icon={<IconBrandWhatsapp size={32} color="#8764BB" />}
        primaryCtaText="Add Mobile"
        secondaryCtaText="Cancel"
        onPrimaryCta={() => {
          setShowMissingMobileDialog(false);
          setShowAddMobileDialog(true);
        }}
        onSecondaryCta={() => setShowMissingMobileDialog(false)}
      />

      {/* Add Email Input Dialog */}
      <MDialog
        isOpen={showAddEmailDialog}
        onClose={() => setShowAddEmailDialog(false)}
        showIconVariant={true}
        icon={<IconMail size={32} color="#8764BB" />}
        showInputVariant={true}
        inputType="email"
        inputPlaceholder="Not Answered"
        inputLabel="Email"
        showLabel={true}
        inputValue={emailInput}
        onInputChange={setEmailInput}
        primaryCtaText="Verify"
        onPrimaryCta={handleVerifyEmail}
        isPrimaryCtaDisabled={!isValidEmail(emailInput)}
      />

      {/* Add Mobile Input Dialog */}
      <MDialog
        isOpen={showAddMobileDialog}
        onClose={() => setShowAddMobileDialog(false)}
        showIconVariant={true}
        icon={<IconBrandWhatsapp size={32} color="#8764BB" />}
        showInputVariant={true}
        inputType="tel"
        inputPlaceholder="Enter mobile number"
        inputLabel="Mobile Number"
        showLabel={true}
        showDialCode={true}
        dialCode={dialCode}
        inputValue={mobileInput}
        onInputChange={setMobileInput}
        primaryCtaText="Verify"
        onPrimaryCta={handleVerifyMobile}
        isPrimaryCtaDisabled={!isValidMobile(mobileInput)}
      />

      {/* Email OTP Drawer */}
      <MOtp
        open={showEmailOtpDrawer}
        onClose={() => setShowEmailOtpDrawer(false)}
        onDone={handleEmailOtpVerify}
        onResend={handleEmailResend}
        onEdit={handleEmailEdit}
        title="Verify Email Address"
        sendTo={emailInput}
        resendTimer={emailResendTimer}
      />

      {/* Mobile OTP Drawer */}
      <MOtp
        open={showMobileOtpDrawer}
        onClose={() => setShowMobileOtpDrawer(false)}
        onDone={handleMobileOtpVerify}
        onResend={handleMobileResend}
        onEdit={handleMobileEdit}
        title="Verify Mobile Number"
        sendTo={`${dialCode} ${mobileInput}`}
        resendTimer={mobileResendTimer}
      />

      {/* Success Dialog */}
      <MDialog
        isOpen={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        showIconVariant={true}
        icon={
          successType === "email" ? (
            <IconMail size={32} color="#8764BB" />
          ) : (
            <IconBrandWhatsapp size={32} color="#8764BB" />
          )
        }
        title={
          successType === "email"
            ? "Email verification successful"
            : "Mobile verification successful"
        }
        description={
          successType === "email"
            ? "You can now directly send documents by email to Maatra"
            : "You can now directly send documents via WhatsApp to Maatra"
        }
        primaryCtaText="Send Document"
        onPrimaryCta={handleSuccessDialogClose}
      />
    </div>
  );
};

export default DocumentSend;
